import instagramIcon from "../images/platform_icons/instagram.png";
import tiktokIcon from "../images/platform_icons/tiktok.png";
import youtubeIcon from "../images/platform_icons/youtube.png";
import facebookIcon from "../images/platform_icons/facebook.png";
import telegramIcon from "../images/platform_icons/telegram.png";
import xIcon from "../images/platform_icons/x.png";

export interface ServiceFaqItem {
  id: number;
  title: string;
  content: string;
}

export interface Service {
  id: number;
  slug: string;
  title: string;
  shortDesc: string;
  logo: string;
  brandColor: string;
  intro: string;
  body: string;
  outcomes: string[];
  outcomeIntro: string;
  faqItems: ServiceFaqItem[];
}

const services: Service[] = [
  {
    id: 1,
    slug: "instagram",
    title: "Instagram Services",
    shortDesc:
      "Buy real Instagram followers, likes, and views. Cheap prices and fast delivery every time.",
    logo: instagramIcon,
    brandColor: "#E4405F",
    intro:
      "Cheap Instagram services for creators, resellers, and small brands. Followers, likes, reel views, story views, saves, and comments — all priced to leave real margin and delivered fast.",
    body:
      "Instagram is the platform where quality actually matters. A drop rate you can't control turns a 5k follower order into a support ticket queue. We price every Instagram service with the drop rate shown on the order page, so you know what you're buying before you click. Most orders start in under a minute. Refills run automatically on services that need them.",
    outcomeIntro:
      "Here's what our Instagram catalog covers. Everything runs on our direct supply, so prices stay low and delivery stays clean.",
    outcomes: [
      "Real Instagram Followers.",
      "Reel Views And Saves.",
      "Story Views And Poll Votes.",
      "Post Likes And Comments.",
      "Hashtag And Impression Boosts.",
      "Automatic Refill Where Supported.",
    ],
    faqItems: [
      {
        id: 1,
        title: "01. Pick Your Service",
        content:
          "Browse the Instagram catalog and pick the service you need. Every service shows the price per 1000, the drop rate, and the refill policy before you order.",
      },
      {
        id: 2,
        title: "02. Paste Your Link",
        content:
          "Paste the profile, post, or reel link. No password needed, ever. We never ask for your login — all services work on public links.",
      },
      {
        id: 3,
        title: "03. Order Starts In Seconds",
        content:
          "Most Instagram orders enter processing within 30 seconds of payment. Larger orders drip naturally over hours or days so growth looks smooth.",
      },
      {
        id: 4,
        title: "04. Track And Refill",
        content:
          "Watch progress in your dashboard. If an order needs a refill, hit the button. Supported services refill automatically without a ticket.",
      },
    ],
  },
  {
    id: 2,
    slug: "tiktok",
    title: "TikTok Services",
    shortDesc:
      "Boost TikTok videos with likes, views, and followers. Orders start in seconds.",
    logo: tiktokIcon,
    brandColor: "#000000",
    intro:
      "Cheap TikTok services priced for creators and resellers who want to push real clips into more feeds. Views, likes, followers, shares, and live viewers — all with fast delivery.",
    body:
      "TikTok is a volume game. A few thousand early views on a strong clip can tell the algorithm to show it to more people, and the cheapest way to do that is through a panel that actually delivers. We start TikTok orders in seconds and keep prices low enough to run the same test across ten videos in the same week.",
    outcomeIntro:
      "Our TikTok catalog is built for creators who test a lot and resellers who need stable margin on bulk orders.",
    outcomes: [
      "Cheap TikTok Views.",
      "Real TikTok Likes.",
      "TikTok Followers With Refill.",
      "Shares And Saves.",
      "Live Stream Viewers.",
      "Comment Packages.",
    ],
    faqItems: [
      {
        id: 1,
        title: "01. Find The Service",
        content:
          "Open the TikTok section of the panel. Every service lists the delivery speed and the price per 1000 up front. No hidden fees.",
      },
      {
        id: 2,
        title: "02. Drop Your Video Link",
        content:
          "Paste the TikTok video URL, not your profile. For followers, paste the profile URL. The panel validates the link before the order enters the queue.",
      },
      {
        id: 3,
        title: "03. Delivery Starts Fast",
        content:
          "Most TikTok orders begin delivery within one minute. Views land in under an hour for standard orders. Larger ones spread out across a few hours to look natural.",
      },
      {
        id: 4,
        title: "04. Scale What Works",
        content:
          "Use cheap orders to test which videos are worth pushing harder. When a clip starts climbing, hit it again with a bigger order and ride the algorithm.",
      },
    ],
  },
  {
    id: 3,
    slug: "youtube",
    title: "YouTube Services",
    shortDesc:
      "Grow your channel with real subscribers, views, and watch time. Safe and steady delivery.",
    logo: youtubeIcon,
    brandColor: "#FF0000",
    intro:
      "Cheap YouTube services for channels chasing the monetisation bar and creators who need real watch time. Subscribers, views, likes, comments, and minutes watched — priced to keep growing.",
    body:
      "YouTube cares about watch time more than any other metric, and that's where most cheap panels cut corners. We run watch-time services that actually hold, not the ones that drop the moment an order finishes. Delivery runs slow on purpose to match natural growth patterns and pass YouTube's spam filters.",
    outcomeIntro:
      "Services built for channels that need to hit thresholds and look natural doing it.",
    outcomes: [
      "Monetisation-Safe Subscribers.",
      "Real Watch Time Minutes.",
      "Video Views With Retention.",
      "Likes And Comments.",
      "Shorts Views And Likes.",
      "Live Stream Viewers.",
    ],
    faqItems: [
      {
        id: 1,
        title: "01. Pick A Channel Service",
        content:
          "Subscriber services need your channel URL. Video services need the video URL. Watch time services work on specific videos, not the whole channel.",
      },
      {
        id: 2,
        title: "02. Paste And Pay",
        content:
          "Drop the link and confirm the order. For watch time, pick the minutes-per-view option that fits the video length. The panel handles the rest.",
      },
      {
        id: 3,
        title: "03. Steady Delivery",
        content:
          "YouTube orders deliver slowly on purpose. A 1000-subscriber order spreads over a few days. Faster delivery raises flags, and flags lose channels.",
      },
      {
        id: 4,
        title: "04. Track In Your Dashboard",
        content:
          "Every order shows live progress. Once delivery finishes, check YouTube Studio and confirm the numbers match. Refills run on supported services.",
      },
    ],
  },
  {
    id: 4,
    slug: "facebook",
    title: "Facebook Services",
    shortDesc:
      "Page likes, post likes, video views, and more. Cheap rates for pages and ads.",
    logo: facebookIcon,
    brandColor: "#1877F2",
    intro:
      "Cheap Facebook services for pages, groups, and creators. Page likes, post reactions, video views, event attendees, and followers — priced for agencies and resellers running client accounts.",
    body:
      "Facebook is the platform where service quality varies the most, and half the panels in the market push stale bot accounts that Facebook flags within hours. We run cleaner Facebook services on direct supply, so the accounts look real and the page metrics actually hold.",
    outcomeIntro:
      "A full Facebook catalog for pages, posts, and groups. Everything priced for bulk.",
    outcomes: [
      "Page Likes And Followers.",
      "Post Likes And Reactions.",
      "Video Views On Posts And Reels.",
      "Group Members.",
      "Event Attendees And Interested.",
      "Comment And Share Packages.",
    ],
    faqItems: [
      {
        id: 1,
        title: "01. Choose Page Or Post",
        content:
          "Page services need the page URL. Post services need the direct post link. Group and event services work the same way — paste the link and order.",
      },
      {
        id: 2,
        title: "02. Public Content Only",
        content:
          "Facebook services only work on public pages and public posts. If the content is set to friends-only or private, the order won't process.",
      },
      {
        id: 3,
        title: "03. Delivery Starts Quickly",
        content:
          "Most Facebook orders start within a few minutes. Larger page-like orders spread across several hours to stay clean in Facebook's spam filters.",
      },
      {
        id: 4,
        title: "04. Refill And Support",
        content:
          "If any order drops outside the expected range, hit refill in your dashboard or open a ticket. Support replies from real people, not bots.",
      },
    ],
  },
  {
    id: 5,
    slug: "telegram",
    title: "Telegram Services",
    shortDesc:
      "Members, views, and reactions for channels and groups. Simple refill on every order.",
    logo: telegramIcon,
    brandColor: "#26A5E4",
    intro:
      "Cheap Telegram services for channels, groups, and public chats. Members, post views, reactions, and poll votes — delivered fast with stable pricing.",
    body:
      "Telegram is the easiest platform to grow on a panel, and also the easiest to get scammed on. Most Telegram services advertised at pennies are just empty accounts that leave the channel within a day. Our Telegram supply runs on direct sources, so members stay and post views count properly.",
    outcomeIntro:
      "Services for Telegram owners who want real numbers on their channels and groups.",
    outcomes: [
      "Channel And Group Members.",
      "Post Views With Reactions.",
      "Targeted Country Members.",
      "Poll Vote Packages.",
      "Bot Starts And Subscriptions.",
      "Emoji Reaction Bundles.",
    ],
    faqItems: [
      {
        id: 1,
        title: "01. Grab The Link",
        content:
          "Copy your Telegram channel or group link. Public channels only — private invite links won't process through the panel.",
      },
      {
        id: 2,
        title: "02. Pick Members Or Views",
        content:
          "Members join your channel. Post views attach to a specific post link. Pick the right service for what you need and paste the link.",
      },
      {
        id: 3,
        title: "03. Fast Start",
        content:
          "Telegram orders are the fastest on the panel. Views land in seconds. Members join within a few minutes for small orders, longer for bigger ones.",
      },
      {
        id: 4,
        title: "04. Stable Delivery",
        content:
          "Our Telegram members are direct supply, not recycled bots. The drop rate stays low and refills are automatic on supported services.",
      },
    ],
  },
  {
    id: 6,
    slug: "twitter-x",
    title: "Twitter / X Services",
    shortDesc:
      "Followers, likes, and views for X accounts. Low prices for creators and brands.",
    logo: xIcon,
    brandColor: "#000000",
    intro:
      "Cheap X services for accounts building an audience after the Twitter rebrand. Followers, post likes, reposts, impressions, and video views — priced for creators who post a lot.",
    body:
      "X changed its algorithm three times in the last year and pushed engagement signals to the top of the feed. That means a few hundred likes in the first hour matters more on X than on any other platform. Our X services deliver fast so your posts clear the initial engagement gate and land in more feeds.",
    outcomeIntro:
      "Services for X creators who need early engagement and steady growth.",
    outcomes: [
      "Real X Followers.",
      "Post Likes And Retweets.",
      "Video And Tweet Impressions.",
      "Reply And Quote Packages.",
      "Bookmark Bundles.",
      "Premium Audience Followers.",
    ],
    faqItems: [
      {
        id: 1,
        title: "01. Find The Service",
        content:
          "Open the X section of the panel. Every service lists the delivery speed, price per 1000, and refill policy before you order.",
      },
      {
        id: 2,
        title: "02. Paste The Link",
        content:
          "Follower services take the profile URL. Post services take the tweet URL. X services only work on public accounts and public posts.",
      },
      {
        id: 3,
        title: "03. Quick Engagement",
        content:
          "Likes and retweets land within minutes — important for X since early engagement decides whether a post climbs the algorithm.",
      },
      {
        id: 4,
        title: "04. Keep Posting",
        content:
          "Panel services amplify good posts. Pair them with real content, post consistently, and let the first hour of engagement carry the rest.",
      },
    ],
  },
];

export default services;
